<div class="agenthome-page" [class.agenthome-page--panel-open]="artifacts.panelOpen()">

  <!-- Hidden file input shared by welcome and active-chat upload buttons.
       The accept attribute is set dynamically per agent via uploadAccept(). -->
  <input #aiRoleFileInput type="file" hidden
    accept=".pdf,.docx,.txt,.vtt,.srt,.md,.xlsx,.csv,.tsv"
    (change)="onFileSelected($event)">

  <!-- ── Left Panel ─────────────────────────────────────────── -->
  <!-- Below md, the sidebar forces the collapsed 56px rail; expand becomes a
       <p-drawer> overlay (see below). Above md, existing behavior. -->
  <aside class="ah-sidebar" [class.ah-sidebar--collapsed]="railMode()">
    <ng-container *ngTemplateOutlet="sidebarBody; context: { inDrawer: false }"></ng-container>
  </aside>

  <!-- Mobile drawer — projects the same sidebar body, always in expanded mode. -->
  <p-drawer [visible]="drawerOpen()" (visibleChange)="drawerOpen.set($event)" position="left" styleClass="ah-mobile-drawer" [style]="{ width: 'min(320px, 85vw)' }">
    <ng-container *ngTemplateOutlet="sidebarBody; context: { inDrawer: true }"></ng-container>
  </p-drawer>

  <!-- Sidebar body template. Rendered inline into <aside> above on desktop,
       and (as of the mobile drawer work) into <p-drawer> when below md.
       `inDrawer` overrides the rail-mode signal — drawers always show the
       expanded content, never the 56px rail. -->
  <ng-template #sidebarBody let-inDrawer="inDrawer">

    <!-- Rail: shown only when collapsed -->
    <div class="ah-sidebar__rail" [hidden]="inDrawer || !railMode()">

      <!-- Logo → panel icon on hover -->
      <button class="ah-rail-btn ah-rail-btn--brand" type="button" (click)="toggleSidebar()" title="Expand sidebar">
        <img src="assets/WatchGuard_Logo_Black_Red.png" alt="WatchGuard" class="ah-rail-logo ah-rail-logo--default" />
        <svg class="ah-rail-expand-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M14 2a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h12zM2 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2H2z"/>
          <path d="M3 4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4z"/>
        </svg>
      </button>

      <!-- Top cluster: primary actions — mirrors the top of the expanded
           sidebar (New chat → Agent Catalog → Search). -->
      <button class="ah-rail-btn" type="button" (click)="onNewChat()" title="New chat">
        <i class="pi pi-plus"></i>
      </button>
      <button class="ah-rail-btn" type="button" (click)="onOpenCatalog()" title="Agent Catalog">
        <i class="pi pi-th-large"></i>
      </button>
      <button class="ah-rail-btn" type="button" (click)="openSessionSearch()" title="Search sessions (Ctrl+K)">
        <i class="pi pi-search"></i>
      </button>

      <!-- Spacer -->
      <div class="ah-rail__spacer"></div>

      <!-- Bottom cluster mirrors the expanded footer EXACTLY so nothing shifts
           vertically when you collapse/expand: Switch app (Studio / Admin /
           Dev Tools / Apps) → Workspace → Dashboards → Send Feedback → avatar.
           The single divider echoes the expanded footer's top border. -->
      <div class="ah-rail__divider"></div>
      @if (isStudio()) {
        <button class="ah-rail-btn ah-rail-btn--app" type="button" (click)="openStudioPortal()" title="Open Studio ↗">
          <i class="pi pi-sparkles"></i>
        </button>
      }
      @if (isAdmin()) {
        <button class="ah-rail-btn" type="button" (click)="openAdminPortal()" title="Open Admin Portal ↗">
          <i class="pi pi-shield"></i>
        </button>
      }
      @if (isDevTools()) {
        <button class="ah-rail-btn" type="button" (click)="openDevToolsPortal()" title="Open Dev Tools ↗">
          <i class="pi pi-wrench"></i>
        </button>
      }
      <!-- Apps sits with the portal launchers, not the --data cluster: it opens
           the sibling apps portal in a new tab, it is not an in-hub route. -->
      @if (showApps()) {
        <button type="button" (click)="openAppsPortal()"
          class="ah-rail-btn" title="Open Apps ↗">
          <!-- pi-box, NOT pi-th-large — the grid glyph is the Agent Catalog's. -->
          <i class="pi pi-box"></i>
        </button>
      }
      @if (showWorkspace()) {
        <a routerLink="/workspace" routerLinkActive="ah-rail-btn--data-active"
          class="ah-rail-btn ah-rail-btn--data" title="Workspace">
          <i class="pi pi-folder-open"></i>
        </a>
      }
      @if (showDashboards()) {
        <a routerLink="/dashboards" routerLinkActive="ah-rail-btn--data-active"
          class="ah-rail-btn ah-rail-btn--data" title="Dashboards">
          <i class="pi pi-chart-bar"></i>
        </a>
      }
      @if (showAbilities()) {
        <button type="button" class="ah-rail-btn ah-rail-btn--data" title="Abilities"
          (click)="openAbilities()">
          <i class="pi pi-compass"></i>
        </button>
      }

      <!-- Send Feedback -->
      <button class="ah-rail-btn" type="button" (click)="openFeedback()" title="Send Feedback">
        <i class="pi pi-comment"></i>
      </button>

      <!-- User avatar -->
      <div class="ah-rail__user-wrap">
        <button class="ah-rail-btn ah-rail-btn--avatar" type="button"
          (click)="toggleUserMenu($event)" title="{{ userName() }}">
          <app-usage-ring [size]="34" [initials]="userInitials" />
        </button>
        @if (userMenuOpen()) {
          <div class="ah-user-menu ah-user-menu--rail" (click)="$event.stopPropagation()" (mousedown)="$event.stopPropagation()">
            <div class="ah-user-menu__info">
              <div class="ah-user-menu__avatar">{{ userInitials }}</div>
              <div class="ah-user-menu__details">
                <span class="ah-user-menu__name">{{ userName() }}</span>
                @if (userEmail()) { <span class="ah-user-menu__email">{{ userEmail() }}</span> }
              </div>
            </div>
            <div class="ah-user-menu__divider"></div>
            <div class="ah-user-menu__theme">
              <span class="ah-user-menu__theme-label">Theme</span>
              <div class="ah-theme-switch" role="group" aria-label="Theme">
                @for (opt of themeOptions; track opt.mode) {
                  <button type="button" class="ah-theme-switch__btn"
                    [class.ah-theme-switch__btn--active]="theme.mode() === opt.mode"
                    [attr.aria-pressed]="theme.mode() === opt.mode"
                    [title]="opt.label" (click)="setTheme(opt.mode)">
                    <i [class]="opt.icon"></i>
                    <span>{{ opt.label }}</span>
                  </button>
                }
              </div>
            </div>
            <div class="ah-user-menu__divider"></div>
            <app-usage-meter></app-usage-meter>
            <div class="ah-user-menu__divider"></div>
            <button class="ah-user-menu__logout" type="button" (click)="onLogout()">
              <i class="pi pi-sign-out"></i> Log out
            </button>
          </div>
        }
      </div>

    </div>

    <div class="ah-sidebar__content" [hidden]="!inDrawer && railMode()">

      <!-- Brand header -->
      <div class="ah-sidebar__brand">
        <a routerLink="/chat" class="ah-brand-link">
          <img src="assets/WatchGuard_Logo_Black_Red.png" alt="WatchGuard" class="ah-brand-logo" />
          <span class="ah-brand-text">Agent Hub Home</span>
        </a>
        <div class="ah-sidebar__brand-actions">
          <button class="ah-sidebar__collapse-btn" type="button" (click)="toggleSidebar()" title="Collapse sidebar">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M0 3a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3zm5-1v12h9a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1H5zm-1 0H2a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h2V2z"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Session search + list -->
      <div class="ah-sidebar__main">

        <div class="ah-sidebar__section">
          <!-- New chat is the core action → top -->
          <button class="ah-new-chat-btn" type="button" data-tour="new-chat" (click)="onNewChat()">
            <i class="pi pi-plus"></i>
            New chat
          </button>

          <button class="ah-catalog-btn" type="button" data-tour="agent-catalog" (click)="onOpenCatalog()">
            <i class="pi pi-th-large"></i>
            <span style="flex:1; text-align:left;">Agent Catalog</span>
            @if (whatsNew.hasUnseenBadge('agent-catalog')) {
              <span class="ah-nav-new-pill">New</span>
            }
          </button>

          <!-- Search sits below the catalog, above Recents -->
          <div class="ah-sidebar__search-row">
            <button class="ah-search-trigger" type="button" data-tour="search-sessions" (click)="openSessionSearch()">
              <i class="pi pi-search"></i>
              <span>Search sessions…</span>
              <kbd class="ah-search-trigger__kbd">Ctrl+K</kbd>
            </button>
          </div>

          <!-- Recents head: collapse toggle (chevron beside the label) + group-by -->
          <div class="ah-sidebar__section-head">
            <button type="button" class="ah-sidebar__section-toggle"
              (click)="toggleSessionsSection()"
              [attr.aria-expanded]="sessionsSectionExpanded()">
              <span class="ah-sidebar__section-header">Recents</span>
              <i class="pi" [class.pi-chevron-down]="sessionsSectionExpanded()" [class.pi-chevron-right]="!sessionsSectionExpanded()"></i>
            </button>

            <div class="ah-groupby">
              <button type="button" class="ah-groupby__trigger"
                [class.ah-groupby__trigger--active]="groupBy() !== 'none'"
                (click)="toggleGroupByMenu($event)"
                [attr.aria-expanded]="groupByMenuOpen()"
                title="Group by" aria-label="Group by">
                <i class="pi pi-sliders-h"></i>
              </button>
              @if (groupByMenuOpen()) {
                <div class="ah-groupby__menu" (click)="$event.stopPropagation()">
                  <span class="ah-groupby__label">Group by</span>
                  <button type="button" class="ah-groupby__opt"
                    [class.ah-groupby__opt--active]="groupBy() === 'none'"
                    (click)="setGroupBy('none')">
                    <span>None</span>
                    @if (groupBy() === 'none') { <i class="pi pi-check"></i> }
                  </button>
                  <button type="button" class="ah-groupby__opt"
                    [class.ah-groupby__opt--active]="groupBy() === 'date'"
                    (click)="setGroupBy('date')">
                    <span>Date</span>
                    @if (groupBy() === 'date') { <i class="pi pi-check"></i> }
                  </button>
                </div>
              }
            </div>
          </div>

          @if (sessionsSectionExpanded()) {
          @if (sessionsLoading()) {
            <div class="ah-sessions-loading">
              @for (_ of [1,2,3]; track $index) {
                <div class="ah-session-skeleton"></div>
              }
            </div>
          } @else if (filteredSessions.length === 0) {
            <p class="ah-sessions-empty">No sessions yet. Start a chat!</p>
          } @else {
            @for (group of sessionGroups; track group.key) {
              <div class="ah-session-group">
                @if (group.label) {
                  <span class="ah-session-group__head">{{ group.label }}</span>
                }
                <div class="ah-session-list">
                  @for (session of group.sessions; track session.session_id) {
                    <div class="ah-session-item"
                      [class.ah-session-item--active]="sessionId() === session.session_id"
                      [class.ah-session-item--confirming]="confirmDeleteId() === session.session_id">
                      @if (confirmDeleteId() === session.session_id) {
                        <div class="ah-session-item__confirm">
                          <span class="ah-session-item__confirm-label">Delete?</span>
                          <button class="ah-session-item__confirm-yes" type="button"
                            (click)="onConfirmDelete(session)">Yes</button>
                          <button class="ah-session-item__confirm-no" type="button"
                            (click)="onCancelDelete()">No</button>
                        </div>
                      } @else if (renameId() === session.session_id) {
                        <input class="ah-session-item__rename-input" type="text"
                          [value]="renameDraft()" maxlength="60" autofocus
                          aria-label="Rename session"
                          (click)="$event.stopPropagation()"
                          (input)="renameDraft.set($any($event.target).value)"
                          (keydown.enter)="onConfirmRename(session)"
                          (keydown.escape)="onCancelRename()"
                          (blur)="onConfirmRename(session)" />
                      } @else {
                        <button class="ah-session-item__select" type="button"
                          (click)="onSelectSession(session)"
                          [title]="session.title || 'Untitled'">
                          <i class="pi {{ sessionIcon(session.active_agent_id) }} ah-session-item__icon" aria-hidden="true"></i>
                          <span class="ah-session-item__title">{{ session.title || 'Untitled' }}</span>
                          <span class="ah-session-item__time">{{ relativeTime(session.updated_at) }}</span>
                        </button>
                        <button class="ah-session-item__rename" type="button"
                          title="Rename session" (click)="onStartRename(session)">
                          <i class="pi pi-pencil"></i>
                        </button>
                        <button class="ah-session-item__delete" type="button"
                          title="Delete session" (click)="onDeleteSession(session)">
                          <i class="pi pi-trash"></i>
                        </button>
                      }
                    </div>
                  }
                </div>
              </div>
            }
          }
          }

          <button type="button" class="ah-all-sessions-btn" (click)="openSessionsManager()">
            <i class="pi pi-list"></i>
            All sessions
          </button>
        </div>

      </div>

      <!-- Footer: app launcher dock + user profile (Theme lives in the profile menu) -->
      <div class="ah-sidebar__footer">

        <!-- ── Switch app dock: Studio / Admin / Dev Tools / Apps are separate
             WatchGuard portals, surfaced here as always-visible app tiles (↗)
             rather than buried in the profile menu. Only tiles the caller can
             reach are shown.

             Apps belongs here, not in the footer nav below: since the apps
             migration it is the sibling wg-agents-apps-ui portal opened in a new
             tab, so styling it like the in-hub routes (Workspace / Dashboards /
             Abilities) promised navigation it does not do. ── -->
        @if (isStudio() || isAdmin() || isDevTools() || showApps()) {
          <div class="ah-app-dock" data-tour="switch-app">
            <div class="ah-app-dock__label">
              <span>Switch app</span>
              <span class="ah-app-dock__rule"></span>
            </div>
            <div class="ah-app-dock__tiles">
              @if (isStudio()) {
                <button class="ah-app-tile ah-app-tile--studio" type="button"
                  (click)="openStudioPortal()" title="Open Studio (opens in a new tab)">
                  <span class="ah-app-tile__icon"><i class="pi pi-sparkles"></i></span>
                  <span class="ah-app-tile__text">
                    <span class="ah-app-tile__name">Studio</span>
                    <span class="ah-app-tile__sub">Build agents</span>
                  </span>
                  @if (whatsNew.hasUnseenBadge('studio')) {
                    <span class="ah-nav-new-pill ah-nav-new-pill--tile">New</span>
                  }
                  <i class="pi pi-arrow-up-right ah-app-tile__jump"></i>
                </button>
              }
              @if (isAdmin()) {
                <button class="ah-app-tile" type="button"
                  (click)="openAdminPortal()" title="Open Admin Portal (opens in a new tab)">
                  <span class="ah-app-tile__icon"><i class="pi pi-shield"></i></span>
                  <span class="ah-app-tile__text">
                    <span class="ah-app-tile__name">Admin</span>
                    <span class="ah-app-tile__sub">Governance</span>
                  </span>
                  @if (whatsNew.hasUnseenBadge('admin')) {
                    <span class="ah-nav-new-pill ah-nav-new-pill--tile">New</span>
                  }
                  <i class="pi pi-arrow-up-right ah-app-tile__jump"></i>
                </button>
              }
              @if (isDevTools()) {
                <button class="ah-app-tile" type="button"
                  (click)="openDevToolsPortal()" title="Open Dev Tools (opens in a new tab)">
                  <span class="ah-app-tile__icon"><i class="pi pi-wrench"></i></span>
                  <span class="ah-app-tile__text">
                    <span class="ah-app-tile__name">Dev Tools</span>
                    <span class="ah-app-tile__sub">Platform config</span>
                  </span>
                  <i class="pi pi-arrow-up-right ah-app-tile__jump"></i>
                </button>
              }
              @if (showApps()) {
                <button class="ah-app-tile" type="button"
                  (click)="openAppsPortal()" title="Open Apps (opens in a new tab)">
                  <!-- pi-box, NOT pi-th-large — the grid glyph is the Agent Catalog's. -->
                  <span class="ah-app-tile__icon"><i class="pi pi-box"></i></span>
                  <span class="ah-app-tile__text">
                    <span class="ah-app-tile__name">Apps</span>
                    <span class="ah-app-tile__sub">Interactive tools</span>
                  </span>
                  @if (whatsNew.hasUnseenBadge('apps')) {
                    <span class="ah-nav-new-pill ah-nav-new-pill--tile">New</span>
                  }
                  <i class="pi pi-arrow-up-right ah-app-tile__jump"></i>
                </button>
              }
            </div>
          </div>
        }

        <!-- Workspace / Dashboards live here in the footer (above Send Feedback)
             so they're always visible instead of hidden inside the profile menu.
             Workspace = agent outputs (report library + data tables).
             These are in-hub routes; Apps is a separate portal and lives in the
             Switch app dock above. -->
        @if (showWorkspace()) {
          <a routerLink="/workspace" routerLinkActive="ah-footer-nav-item--active"
            class="ah-footer-nav-item">
            <i class="pi pi-folder-open"></i> <span style="flex:1; text-align:left;">Workspace</span>
            @if (whatsNew.hasUnseenBadge('workspace')) {
              <span class="ah-nav-new-pill">New</span>
            }
          </a>
        }
        @if (showDashboards()) {
          <a routerLink="/dashboards" routerLinkActive="ah-footer-nav-item--active"
            class="ah-footer-nav-item">
            <i class="pi pi-chart-bar"></i> <span style="flex:1; text-align:left;">Dashboards</span>
            @if (whatsNew.hasUnseenBadge('dashboards')) {
              <span class="ah-nav-new-pill">New</span>
            }
          </a>
        }
        @if (showAbilities()) {
          <button type="button" class="ah-footer-nav-item" data-tour="abilities" (click)="openAbilities()">
            <i class="pi pi-compass"></i> <span style="flex:1; text-align:left;">Abilities</span>
            <!-- Launch pill driven by the What's New registry (navBadge anchor):
                 shows only while the announcement is unseen, then self-expires. -->
            @if (whatsNew.hasUnseenBadge('abilities')) {
              <span class="ah-nav-new-pill">New</span>
            }
          </button>
        }
        <!-- Per-user OAuth connectors. Gated on the connector catalog being
             non-empty (the connectors_enabled flag is server-side; an empty
             catalog means the feature is dark in this env). -->
        @if (connections.available()) {
          <button type="button" class="ah-footer-nav-item"
            [class.ah-footer-nav-item--active]="connections.modalOpen()"
            (click)="connections.triggerOpen()">
            <i class="pi pi-link"></i> <span style="flex:1; text-align:left;">Connections</span>
            @if (whatsNew.hasUnseenBadge('connections')) {
              <span class="ah-nav-new-pill">New</span>
            }
          </button>
        }

        <!-- Send Feedback -->
        <button class="ah-feedback-btn" type="button" data-tour="send-feedback" (click)="openFeedback()">
          <i class="pi pi-comment"></i>
          <span>Send Feedback</span>
        </button>

        <div class="ah-sidebar__user-wrap">
          <!-- Footer user row: profile button + always-visible Theme toggle and
               Log out icons (surfaced directly, like the Admin portal footer,
               instead of being buried in the profile menu). -->
          <div class="ah-user-row" data-tour="profile">
            <button class="ah-user-btn" type="button"
              (click)="toggleUserMenu($event)"
              [attr.aria-expanded]="userMenuOpen()"
              aria-haspopup="true">
              <app-usage-ring [size]="34" [initials]="userInitials" />
              <div class="ah-user-btn__info">
                <span class="ah-user-btn__name">{{ firstName }}</span>
                @if (userEmail()) {
                  <span class="ah-user-btn__email">{{ userEmail() }}</span>
                }
              </div>
              <i class="pi pi-ellipsis-h ah-user-btn__dots"></i>
            </button>

            <!-- What's New: full changelog on demand; the dot flags unseen items. -->
            <button class="ah-user-icon-btn ah-whatsnew-btn" type="button" (click)="openWhatsNew()"
              title="What's new" aria-label="What's new">
              <i class="pi pi-sparkles"></i>
              @if (whatsNew.hasUnseen()) {
                <span class="ah-whatsnew-btn__dot"></span>
              }
            </button>

            <button class="ah-user-icon-btn" type="button" (click)="toggleTheme()"
              [attr.title]="theme.isDark() ? 'Light mode' : 'Dark mode'"
              [attr.aria-label]="theme.isDark() ? 'Switch to light theme' : 'Switch to dark theme'">
              <i class="pi" [class.pi-moon]="!theme.isDark()" [class.pi-sun]="theme.isDark()"></i>
            </button>

            <button class="ah-user-icon-btn" type="button" (click)="onLogout()"
              title="Log out" aria-label="Log out">
              <i class="pi pi-sign-out"></i>
            </button>
          </div>

          @if (userMenuOpen()) {
            <div class="ah-user-menu" (click)="$event.stopPropagation()" (mousedown)="$event.stopPropagation()">
              <app-usage-meter></app-usage-meter>
              @if (showStats()) {
                <app-time-saved variant="card" />
              }
            </div>
          }
        </div>
      </div>

    </div>

  </ng-template>

  <!-- ── Main Panel ─────────────────────────────────────────── -->
  <div class="ah-main">

    <!-- Floating Help button: visible to every authenticated user, anchored
         top-right of the chat pane. Opens the Help portal in a new tab with
         a hash-fragment session (see openHelpPortal). -->
    <button class="ah-help-fab" type="button"
            (click)="openHelpPortal()"
            title="Open Help ↗"
            [attr.aria-label]="whatsNew.hasUnseenBadge('help') ? 'Open Help — new' : 'Open Help'">
      <i class="pi pi-question-circle"></i>
      <!-- "New" indicator driven by the What's New registry (navBadge anchor
           'help'). The FAB is icon-only, so a dot stands in for the pill the
           sidebar nav items use; it self-clears once the entry is seen. -->
      @if (whatsNew.hasUnseenBadge('help')) {
        <span class="ah-help-fab__new-dot" aria-hidden="true"></span>
      }
    </button>

    @if (sessionId() === null) {

      <!-- Welcome view -->
      <div class="ah-welcome">
        <div class="ah-welcome__icon">
          <!-- Sparkles / AI icon (Heroicons solid) -->
          <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"/>
            <path d="M18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z"/>
            <path d="M16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"/>
          </svg>
        </div>
        <h2 class="ah-welcome__title">{{ greeting() }}</h2>
        <p class="ah-welcome__subtitle">Ask anything — Agent Hub takes care of the rest.</p>

        <!-- Your time saved / sessions — appears once there's ≥1 qualified session -->
        @if (showStats()) {
          <app-time-saved variant="strip" />
        }

        <!-- Usage warning (approaching/reached) — sits above the composer -->
        <app-usage-warning />

        <div class="ah-input-area">
          @if (slashMenuOpen()) {
            <div class="ah-slash-menu" role="listbox" aria-label="Agent commands">
              @if (slashMenuItems.length === 0) {
                <p class="ah-slash-menu__empty">No matching agents</p>
              } @else {
                @for (item of slashMenuItems; track item.command + item.label + item.prompt + item.insertOnly; let i = $index) {
                  <button type="button" class="ah-slash-item"
                    role="option"
                    [class.ah-slash-item--active]="slashHighlightIdx() === i"
                    [attr.aria-selected]="slashHighlightIdx() === i"
                    (mouseenter)="slashHighlightIdx.set(i)"
                    (mousedown)="$event.preventDefault()"
                    (click)="onSlashItemClick(i)">
                    <div class="ah-slash-item__main">
                      <span class="ah-slash-item__cmd">{{ item.command }}</span>
                      <span class="ah-slash-item__label">{{ item.label }}</span>
                    </div>
                    @if (item.prompt) {
                      <span class="ah-slash-item__preview">{{ item.prompt }}</span>
                    }
                    <span class="ah-slash-item__agent">{{ item.agentName }}</span>
                    @if (slashHighlightIdx() === i) {
                      <kbd class="ah-slash-item__enter">Enter</kbd>
                    }
                  </button>
                }
              }
            </div>
          }

        <div class="ah-welcome__input-wrap" data-tour="composer">
          @if (pendingAttachment()) {
            <div class="ah-attachment-chip">
              <i class="pi pi-paperclip"></i>
              <span class="ah-attachment-chip__name">{{ pendingAttachment()!.filename }}</span>
              <button class="ah-attachment-chip__remove" type="button"
                aria-label="Remove attachment" (click)="clearAttachment()">
                <i class="pi pi-times"></i>
              </button>
            </div>
          }
          <div class="ah-welcome__input-row">
            <textarea #chatTextarea class="ah-input" rows="2"
              placeholder="Message Agent Hub… (type / for commands)"
              [disabled]="isUploading()"
              (input)="onTextareaInput($event)"
              (keydown)="onInputKeydown($event)"></textarea>
            <div class="ah-input-controls">
              <button class="ah-upload-btn" type="button"
                [disabled]="isUploading() || isInFlight()"
                title="Upload a file"
                aria-label="Upload a file"
                (click)="onUploadClick()">
                @if (isUploading()) {
                  <span class="ah-upload-spinner"></span>
                } @else {
                  <i class="pi pi-paperclip"></i>
                }
              </button>
              <span class="ah-route-hint">
                <i class="pi pi-sparkles" aria-hidden="true"></i>
                Auto-routes to the best agent
              </span>
              <button class="ah-send-btn" type="button"
                [disabled]="(!currentInput().trim() && !pendingAttachment()) || isInFlight() || isUploading() || usageBlocked()"
                [title]="usageBlocked() ? usageBlockedLabel() : (isUploading() ? 'Waiting for file upload…' : 'Send (Enter)')"
                (click)="onSendMessage()">
                <i class="pi pi-arrow-up"></i>
              </button>
            </div>
          </div>
        </div>
        </div>

      </div>

    } @else {

      <!-- Active chat -->
      <div class="ah-chat">

        <div class="ah-chat__messages" #messageContainer>
          <div class="ah-chat__messages-inner">
            @for (msg of messages(); track msg.id) {

              @if (msg.role === 'user') {
                <div class="ah-msg ah-msg--user">
                  <div class="ah-msg__bubble">{{ msg.content }}</div>
                </div>
              }

              @if (msg.role === 'agent') {
                <div class="ah-msg ah-msg--agent">
                  <div class="ah-msg__bubble">
                    <!-- Top copy pill: mirrors the one at the bottom so long
                         responses can be copied without scrolling to the end.
                         canCopy() already gates on the reply being ready. -->
                    @if (canCopy(msg, $last)) {
                      <div class="ah-msg__actions ah-msg__actions--top">
                        <button class="ah-msg__action"
                          [class.ah-msg__action--copied]="copiedMsgId() === msg.id"
                          type="button"
                          [attr.aria-label]="copiedMsgId() === msg.id ? 'Copied' : 'Copy response'"
                          (click)="copyMessage(msg)">
                          <i class="pi"
                             [class.pi-copy]="copiedMsgId() !== msg.id"
                             [class.pi-check]="copiedMsgId() === msg.id"
                             aria-hidden="true"></i>
                          {{ copiedMsgId() === msg.id ? 'Copied!' : 'Copy' }}
                        </button>
                      </div>
                    }
                    <!-- Live "Thinking" block (flag-gated reasoning stream).
                         Ephemeral by design: lives only in this view-model —
                         the history resume handler never restores it. Auto-opens
                         while deltas stream, auto-collapses (not removed) when
                         the answer text starts. -->
                    @if (msg.reasoning) {
                      <div class="ah-reasoning" [class.ah-reasoning--open]="msg.reasoningOpen">
                        <button class="ah-reasoning__header" type="button"
                          [attr.aria-expanded]="!!msg.reasoningOpen"
                          (click)="toggleReasoning($index)">
                          <i class="pi pi-chevron-right ah-reasoning__chevron" aria-hidden="true"></i>
                          {{ msg.reasoningAutoCollapsed ? 'Thinking' : 'Thinking…' }}
                        </button>
                        @if (msg.reasoningOpen) {
                          <div class="ah-reasoning__body">{{ msg.reasoning }}</div>
                        }
                      </div>
                    }
                    @if (msg.isThinking && !msg.content && !msg.reasoningOpen) {
                      <span class="ah-typing" role="status" aria-label="Working">
                        <span class="ah-typing__dot"></span>
                        <span class="ah-typing__dot"></span>
                        <span class="ah-typing__dot"></span>
                      </span>
                    } @else if (_msgRenderer(msg) === 'pcheck') {
                      <!-- Pulse Check (base PCHECK or IMPACT_PCHECK): pickers/cards
                           + markdown via the shared renderer. Interactive widgets
                           lock once this is no longer the latest message (isPast).
                           Uses per-message ui.component so history replay is correct
                           even when activeAgentId has changed since this turn. -->
                      <app-pcheck-renderer
                        [msg]="msg"
                        [msgIdx]="$index"
                        [isPast]="!$last"
                        [isProcessing]="isInFlight()"
                        [agentId]="msg.ui?.agentId ?? activeAgentId()"
                        (action)="onPcheckAction($event)" />
                    } @else if (_msgRenderer(msg) === 'airole') {
                      <!-- AI Role Transformation: the renderer owns all rendering —
                           markdown (footer stripped), tool pills, next-action bar,
                           and download buttons. -->
                      <app-ai-role-renderer
                        [msg]="msg"
                        [msgIdx]="$index"
                        [isPast]="!$last"
                        [isProcessing]="isInFlight()"
                        (action)="onAiRoleAction($event)" />
                    } @else {
                      @if (msg.toolActivity?.length) {
                        <app-tool-activity-pills [activities]="msg.toolActivity!" layout="steps"></app-tool-activity-pills>
                      }
                      <!-- The assistant's reply stays in the chat. For report
                           messages this is just the lead-in (chatBody) — the
                           report body itself renders in the side panel. -->
                      @if (chatBody(msg); as body) {
                        <div class="ah-prose" [innerHTML]="body | markdown"></div>
                      }
                      <!-- A report produced this turn (SF Account Summary, NEAT
                           Deal Analysis) is surfaced as a compact card; the report
                           itself renders in the side panel. -->
                      @if (msg.artifact) {
                        <app-artifact-card [artifact]="msg.artifact" (reanalyze)="onReanalyze($event)" />
                      }
                      <!-- NEAT agent-driven deal-context wizard, rendered inline in
                           the thread (like pcheck's pickers). Emits the [Deal Context]
                           preamble back; hidden once the user submits/skips. -->
                      @if (msg.neatDealContext && !msg.neatDealContextDone) {
                        <app-neat-deal-context (analyze)="onNeatDealContext($event, $index)" />
                      }
                      <!-- LICEXTAGENT inline device/SaaS extension wizard. Rendered
                           in the thread like NEAT's wizard; the form hides once the
                           user submits, and the completion card shows on the turn
                           the request is committed. Theme-aware (--wg-* tokens). -->
                      @if ((msg.licextForm && !msg.licextFormSubmitted) || msg.licextFlowComplete) {
                        <app-licext-renderer
                          [msg]="msg"
                          [msgIdx]="$index"
                          [isPast]="!$last"
                          [isProcessing]="isInFlight()"
                          (action)="onLicextAction($event)" />
                      }
                      <!-- Keep an animated indicator on the latest turn while it's
                           still working and has nothing to show yet (e.g. the gap
                           after a "Switched to…" pill while the report generates),
                           so the bubble is never silently blank. -->
                      <!-- Suppressed while the Thinking block is open — the
                           streaming reasoning is already the activity signal. -->
                      @if ($last && isInFlight() && !chatBody(msg) && !msg.artifact && !msg.reasoningOpen) {
                        <span class="ah-typing" role="status" aria-label="Working">
                          <span class="ah-typing__dot"></span>
                          <span class="ah-typing__dot"></span>
                          <span class="ah-typing__dot"></span>
                        </span>
                      }
                      <!-- The user pressed Stop on this reply (onStop). -->
                      @if (msg.isStopped) {
                        <span class="ah-stopped-note" role="status">
                          <i class="pi pi-ban"></i> Stopped
                        </span>
                      }
                    }

                    @if (msg.suggestions?.length) {
                      <div class="ah-suggestions">
                        @for (chip of msg.suggestions!; track chip.agent_id) {
                          <button class="ah-chip" type="button"
                            [disabled]="isInFlight()"
                            (click)="onAgentSuggestionClick(chip)">
                            {{ chip.label }}
                          </button>
                        }
                      </div>
                    }

                    <!-- Confirm-before-write: the runtime proposed write actions and
                         nothing executes until the user answers here. Buttons send the
                         server-built confirm/reject message back verbatim. -->
                    @if (msg.actionProposal; as proposal) {
                      <div class="ah-action-card"
                           [class.ah-action-card--resolved]="!!msg.actionProposalResolved">
                        <div class="ah-action-card__title">{{ proposal.title }}</div>
                        <ul class="ah-action-card__list">
                          @for (a of proposal.actions; track a.id) {
                            <li><code>{{ a.summary }}</code></li>
                          }
                        </ul>
                        @if (!msg.actionProposalResolved) {
                          <div class="ah-action-card__hint">Nothing is written until you confirm.</div>
                          <div class="ah-action-card__buttons">
                            <button class="ah-action-card__btn" type="button"
                              [disabled]="isInFlight()"
                              (click)="onActionProposalAnswer(msg.id, false)">
                              Cancel
                            </button>
                            <button class="ah-action-card__btn ah-action-card__btn--confirm" type="button"
                              [disabled]="isInFlight()"
                              (click)="onActionProposalAnswer(msg.id, true)">
                              Confirm &amp; write ({{ proposal.actions.length }})
                            </button>
                          </div>
                        } @else if (msg.actionProposalResolved === 'confirmed') {
                          <div class="ah-action-card__resolved">✓ Confirmed</div>
                        } @else if (msg.actionProposalResolved === 'rejected') {
                          <div class="ah-action-card__resolved">Cancelled — nothing was written</div>
                        } @else {
                          <div class="ah-action-card__resolved">Superseded by a newer request</div>
                        }
                      </div>
                    }

                    <!-- Per-user OAuth connectors: this ability runs as YOU and
                         you haven't connected the service yet. One click to the
                         Connections page; the turn can simply be retried after. -->
                    @if (msg.connectionRequired; as cr) {
                      <div class="ah-action-card">
                        <div class="ah-action-card__title">
                          <i class="pi pi-link"></i> Connection needed
                        </div>
                        <div class="ah-action-card__hint">
                          <!-- displayNameFor: users see "Microsoft 365 meetings",
                               never the raw registry slug "microsoft-meetings". -->
                          {{ cr.message || 'This ability uses your own access to ' + connections.displayNameFor(cr.connector) + ', which isn\'t connected yet.' }}
                        </div>
                        <div class="ah-action-card__buttons">
                          <!-- Opens the Connections modal over the thread, so the
                               turn stays on screen and can be retried straight
                               after connecting. -->
                          <button type="button" class="ah-action-card__btn ah-action-card__btn--confirm"
                                  (click)="connections.triggerOpen()">
                            Open Connections
                          </button>
                        </div>
                      </div>
                    }

                    <!-- Copy the reply as raw markdown. Sits outside the renderer
                         branches so pcheck/airole/plain messages all get it. -->
                    @if (canCopy(msg, $last)) {
                      <div class="ah-msg__actions">
                        <button class="ah-msg__action"
                          [class.ah-msg__action--copied]="copiedMsgId() === msg.id"
                          type="button"
                          [attr.aria-label]="copiedMsgId() === msg.id ? 'Copied' : 'Copy response'"
                          (click)="copyMessage(msg)">
                          <i class="pi"
                             [class.pi-copy]="copiedMsgId() !== msg.id"
                             [class.pi-check]="copiedMsgId() === msg.id"
                             aria-hidden="true"></i>
                          {{ copiedMsgId() === msg.id ? 'Copied!' : 'Copy' }}
                        </button>
                      </div>
                    }
                  </div>
                </div>
              }

            }
          </div>
        </div>

        <div class="ah-chat__input-footer ah-input-area">
          <!-- Usage warning (approaching/reached) — sits above the composer -->
          <app-usage-warning />
          @if (slashMenuOpen()) {
            <div class="ah-slash-menu" role="listbox" aria-label="Agent commands">
              @if (slashMenuItems.length === 0) {
                <p class="ah-slash-menu__empty">No matching agents</p>
              } @else {
                @for (item of slashMenuItems; track item.command + item.label + item.prompt + item.insertOnly; let i = $index) {
                  <button type="button" class="ah-slash-item"
                    role="option"
                    [class.ah-slash-item--active]="slashHighlightIdx() === i"
                    [attr.aria-selected]="slashHighlightIdx() === i"
                    (mouseenter)="slashHighlightIdx.set(i)"
                    (mousedown)="$event.preventDefault()"
                    (click)="onSlashItemClick(i)">
                    <div class="ah-slash-item__main">
                      <span class="ah-slash-item__cmd">{{ item.command }}</span>
                      <span class="ah-slash-item__label">{{ item.label }}</span>
                    </div>
                    @if (item.prompt) {
                      <span class="ah-slash-item__preview">{{ item.prompt }}</span>
                    }
                    <span class="ah-slash-item__agent">{{ item.agentName }}</span>
                    @if (slashHighlightIdx() === i) {
                      <kbd class="ah-slash-item__enter">Enter</kbd>
                    }
                  </button>
                }
              }
            </div>
          }

          <div class="ah-chat__input-card">

            @if (pendingAttachment()) {
              <div class="ah-attachment-chip">
                <i class="pi pi-paperclip"></i>
                <span class="ah-attachment-chip__name">{{ pendingAttachment()!.filename }}</span>
                <button class="ah-attachment-chip__remove" type="button"
                  aria-label="Remove attachment" (click)="clearAttachment()">
                  <i class="pi pi-times"></i>
                </button>
              </div>
            }

            <div class="ah-chat__input-row">
              <!-- Type-ahead: the textarea stays ENABLED while a turn streams so the
                   user can compose their next message; only the SEND affordances gate
                   on isInFlight (button below + the in-flight guard in onSendMessage,
                   which Enter routes through — the draft is kept, never sent early). -->
              <textarea #chatTextarea class="ah-input" rows="1"
                [placeholder]="slotPickerActive() ? 'Pick a time from the calendar above to continue…' : (contactPickerActive() ? 'Pick a contact from the list above to continue…' : 'Type / for commands, or ask anything…')"
                [disabled]="isUploading() || slotPickerActive() || contactPickerActive()"
                (input)="onTextareaInput($event)"
                (keydown)="onInputKeydown($event)"></textarea>
              <div class="ah-input-controls">
                <button class="ah-upload-btn" type="button"
                  [disabled]="isUploading() || isInFlight()"
                  title="Upload a file"
                  aria-label="Upload a file"
                  (click)="onUploadClick()">
                  @if (isUploading()) {
                    <span class="ah-upload-spinner"></span>
                  } @else {
                    <i class="pi pi-paperclip"></i>
                  }
                </button>
                <span class="ah-input-controls__spacer"></span>
                <!-- Send ⇄ Stop. While a turn streams the send button becomes
                     a Stop button (Esc does the same): the turn is cancelled
                     server-side and the composer unlocks at once — see
                     onStop for the two halves and why both are needed. -->
                @if (isInFlight() && !isUploading()) {
                  <button class="ah-send-btn ah-send-btn--stop" type="button"
                    title="Stop generating (Esc)"
                    aria-label="Stop generating"
                    (click)="onStop()">
                    <i class="pi pi-stop"></i>
                  </button>
                } @else {
                  <button class="ah-send-btn" type="button"
                    [disabled]="(!currentInput().trim() && !pendingAttachment()) || isInFlight() || isUploading() || usageBlocked() || slotPickerActive() || contactPickerActive()"
                    [title]="usageBlocked() ? usageBlockedLabel() : (slotPickerActive() ? 'Pick a time from the calendar to continue' : (contactPickerActive() ? 'Pick a contact from the list to continue' : (isUploading() ? 'Waiting for file upload…' : 'Send (Enter)')))"
                    aria-label="Send message"
                    (click)="onSendMessage()">
                    <i class="pi pi-arrow-up"></i>
                  </button>
                }
              </div>
            </div>
            @if (isUploading() || isInFlight()) {
              <div class="ah-input-hint ah-input-hint--working">
                @if (isUploading()) {
                  <span class="ah-working"><span class="ah-working__dot"></span> Uploading file…</span>
                } @else if (resumePending()) {
                  <span class="ah-working"><span class="ah-working__dot"></span> Still working on your last message — hang tight, or press Stop to cancel it.</span>
                } @else if (stillWorking()) {
                  <span class="ah-working"><span class="ah-working__dot"></span> Still working — first responses can take longer while an agent warms up. Press <kbd>Esc</kbd> to stop.</span>
                } @else {
                  <span class="ah-working"><span class="ah-working__dot"></span> Working… press <kbd>Esc</kbd> to stop.</span>
                }
              </div>
            }
          </div>

          <p class="ah-input-disclaimer">Agent Hub can make mistakes. Verify important results.</p>
        </div>

      </div>
    }

  </div>

  <!-- ── Report panel resize handle ── -->
  @if (artifacts.panelOpen()) {
    <div class="ah-panel-resize-handle"
      (pointerdown)="onPanelResizeStart($event)"
      (pointermove)="onPanelResizeMove($event)"
      (pointerup)="onPanelResizeEnd()"
      (pointercancel)="onPanelResizeEnd()"
      title="Drag to resize panel"
      role="separator"
      aria-label="Resize report panel"
      aria-orientation="vertical">
      <span class="ah-panel-resize-handle__grip" aria-hidden="true">
        <i class="pi pi-arrows-h"></i>
      </span>
    </div>
  }

  <!-- ── Report panel (3rd column) — push/split on wide, overlay on narrow ── -->
  <app-report-panel [style.width]="panelWidth() ? panelWidth() + 'px' : null"
                    [style.flex]="panelWidth() ? '0 0 ' + panelWidth() + 'px' : null" />

  <!-- ── Session search modal (Claude-style) ── -->
  @if (sessionSearchOpen()) {
    <div class="ah-modal-overlay" (click)="closeSessionSearch()">
      <div class="ah-search-modal" (click)="$event.stopPropagation()" (keydown)="onSearchModalKeydown($event)">
        <div class="ah-search-modal__input-wrap">
          <i class="pi pi-search ah-search-modal__icon"></i>
          <input #sessionSearchInput class="ah-search-modal__input" type="text"
            placeholder="Search sessions…"
            [value]="sessionSearch()"
            (input)="onSessionSearchInput($event)" />
          <button class="ah-search-modal__close" type="button" (click)="closeSessionSearch()" aria-label="Close">
            <i class="pi pi-times"></i>
          </button>
        </div>
        <div class="ah-search-modal__results">
          @if (sessionsLoading()) {
            <p class="ah-search-modal__empty">Loading sessions…</p>
          } @else if (modalSearchSessions.length === 0) {
            <p class="ah-search-modal__empty">
              @if (sessionSearch()) {
                No results for "{{ sessionSearch() }}"
              } @else {
                No sessions yet. Start a chat!
              }
            </p>
          } @else {
            @for (session of modalSearchSessions; track session.session_id; let i = $index) {
              <button type="button" class="ah-search-result"
                [class.ah-search-result--active]="searchHighlightIdx() === i"
                (click)="onSearchModalSelect(session)"
                (mouseenter)="searchHighlightIdx.set(i)">
                <i class="pi pi-comments ah-search-result__icon"></i>
                <span class="ah-search-result__title">{{ session.title || 'Untitled' }}</span>
                <span class="ah-search-result__time">{{ relativeTime(session.updated_at) }}</span>
                @if (searchHighlightIdx() === i) {
                  <kbd class="ah-search-result__enter">Enter</kbd>
                }
              </button>
            }
          }
        </div>
      </div>
    </div>
  }

  <!-- ── All sessions / bulk delete ── -->
  @if (sessionsManagerOpen()) {
    <div class="ah-modal-overlay" (click)="closeSessionsManager()">
      <div class="ah-sessions-manager" (click)="$event.stopPropagation()">
        <div class="ah-sessions-manager__header">
          <h2 class="ah-sessions-manager__title">Sessions</h2>
          <button class="ah-sessions-manager__close" type="button" (click)="closeSessionsManager()" aria-label="Close">
            <i class="pi pi-times"></i>
          </button>
        </div>

        <div class="ah-sessions-manager__toolbar">
          <span class="ah-sessions-manager__count">{{ selectedCount }} selected</span>
          <button type="button" class="ah-sessions-manager__link" (click)="toggleSelectAllManager()">
            {{ allManagerSelected ? 'Deselect all' : 'Select all' }}
          </button>
          @if (bulkDeleteConfirm()) {
            <span class="ah-sessions-manager__confirm-label">Delete {{ selectedCount }}?</span>
            <button type="button" class="ah-sessions-manager__btn ah-sessions-manager__btn--danger"
              [disabled]="!selectedCount" (click)="onBulkDeleteConfirm()">Yes, delete</button>
            <button type="button" class="ah-sessions-manager__btn" (click)="onBulkDeleteCancel()">Cancel</button>
          } @else {
            <button type="button" class="ah-sessions-manager__btn ah-sessions-manager__btn--danger"
              [disabled]="!selectedCount" (click)="onBulkDeleteRequest()">Delete</button>
          }
          <button type="button" class="ah-sessions-manager__btn ah-sessions-manager__btn--ghost"
            (click)="closeSessionsManager()">Close</button>
        </div>

        <div class="ah-sessions-manager__search">
          <i class="pi pi-search"></i>
          <input #managerSearchInput type="text" class="ah-sessions-manager__search-input"
            placeholder="Search sessions…"
            [value]="managerSearch()"
            (input)="onManagerSearchInput($event)" />
        </div>

        <div class="ah-sessions-manager__list">
          @if (managerFilteredSessions.length === 0) {
            <p class="ah-sessions-manager__empty">No sessions match your search.</p>
          } @else {
            @for (session of managerFilteredSessions; track session.session_id) {
              <label class="ah-manager-row"
                [class.ah-manager-row--active]="sessionId() === session.session_id">
                <input type="checkbox" class="ah-manager-row__check"
                  [checked]="isSessionSelected(session.session_id)"
                  (change)="toggleSessionSelected(session.session_id, $event)" />
                <button type="button" class="ah-manager-row__body" (click)="onSelectSession(session); closeSessionsManager()">
                  <span class="ah-manager-row__title">{{ session.title || 'Untitled' }}</span>
                  <span class="ah-manager-row__time">{{ relativeTime(session.updated_at) }}</span>
                </button>
              </label>
            }
          }
        </div>
      </div>
    </div>
  }

  @if (feedbackOpen()) {
    <app-send-feedback-dialog
      [userName]="userName()"
      [userEmail]="userEmail()"
      [conversation]="feedbackConversation()"
      [sessionId]="sessionId()"
      [agentId]="activeAgentId()"
      [agentName]="activeAgentName()"
      (close)="closeFeedback()" />
  }

</div>
